<template>
  <Header v-if="!display.mobile.value" />

  <v-container v-if="loadingUsers" fluid style="max-width: 1700px">
    <v-row>
      <v-col cols="12" md="8">
        <v-skeleton-loader type="article" class="mb-4" />
        <v-skeleton-loader type="image" class="mb-4" height="200" />
        <v-skeleton-loader
          type="paragraph"
          class="mb-4"
          :loading="true"
          :lines="6"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader type="card" class="mb-4" height="300" />
      </v-col>
    </v-row>
  </v-container>
  <v-container v-else>
    <div class="view chat" style="margin-top: 40px">
      <div class="chat-container">
        <!-- Sidebar: Active Chats -->
        <v-btn
          class="d-md-none mobile-menu-btn"
          icon
          @click="toggleSidebar"
          style="position: absolute; top: 10px; left: 10px; z-index: 200"
        >
          <v-icon>mdi-menu</v-icon>
        </v-btn>

        <aside :class="['user-list', { show: isSidebarOpen }]">
          <h3>Active Chats</h3>
          <div v-if="loadingUsers" class="loading">
            <v-progress-circular indeterminate />
            <span>Loading chats...</span>
          </div>
          <div v-else>
            <div
              v-for="chat in activeChats"
              :key="chat.id"
              class="user-item"
              :class="{ active: activeChat?.id === chat.id }"
              @click="selectChatMobile(chat)"
            >
              <img
                :src="`${imageUrl}${chat.listing.image}`"
                alt="Listing"
                class="listing-thumb"
              />
              <div class="user-avatar">
                {{ getInitials(chat.seller.fullName) }}
                <span
                  v-if="chat.isOnline"
                  class="online-indicator online"
                ></span>
                <div v-if="chat.unread > 0" class="unread-badge">
                  {{ chat.unread }}
                </div>
              </div>
              <div class="user-info">
                <div class="user-name">{{ chat.seller.fullName }}</div>
                <div class="listing-title">{{ chat.listing.title }}</div>
                <div class="last-message">
                  {{ chat.lastMessage || "No messages yet" }}
                </div>
              </div>
              <div class="timestamp">{{ formatDate(chat.lastUpdated) }}</div>
              <div
                class="seen-status"
                v-if="chat.lastSender === user.id && chat.lastSeenAt"
              >
                Seen
              </div>
            </div>
            <div v-if="!activeChats.length" class="empty">
              No active conversations
            </div>
          </div>
        </aside>

        <!-- Chat Box -->
        <section
          v-if="activeChat"
          :class="['chat-box', { show: !isSidebarOpen }]"
          ref="chatBox"
        >
          <div class="chat-header d-flex d-md-none">
            <v-btn icon @click="toggleSidebar">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <div class="chat-header-title">
              {{ activeChat.seller.fullName }}
            </div>
          </div>
          <div class="messages-container" ref="messagesContainer">
            <div v-if="messagesLoading" class="loading-messages">
              <v-progress-circular indeterminate />
              <span>Loading messages...</span>
            </div>
            <div
              v-for="message in messages"
              :key="message.id + message.sender_id"
              :class="[
                'message',
                { 'current-user': message.sender_id === user.id.toString() },
              ]"
            >
              <template v-if="message.sender_id !== user.id.toString()">
                <div class="other-user-avatar">
                  <img
                    v-if="activeChat.seller.profilePhoto"
                    :src="`${imageUrl}${activeChat.seller.profilePhoto}`"
                    alt="Profile"
                  />
                  <div v-else class="avatar-fallback">
                    {{ getInitials(activeChat.seller.fullName) }}
                  </div>
                </div>
              </template>

              <div class="message-bubble">
                <div class="text">{{ message.description }}</div>
                <div class="time">{{ formatDate(message.date) }}</div>
                <!-- <div class="meta">
                <div class="time">{{ formatDate(message.date) }}</div>
                <div
                  v-if="message.sender_id === user.id.toString()"
                  class="read-receipts"
                >
                  <v-icon v-if="message.isRead" small color="blue"
                    >mdi-check-all</v-icon
                  >
                  <v-icon v-else small>mdi-check</v-icon>
                </div>
              </div> -->
              </div>
            </div>

            <div v-if="isTyping" class="typing-indicator">
              {{ activeChat.seller.fullName }} is typing...
            </div>
          </div>
          <!-- Message Input -->
          <footer
            v-if="activeChat"
            :class="['message-input', { show: !isSidebarOpen }]"
          >
            <form @submit.prevent="sendMessage">
              <v-text-field
                v-model="inputMessage"
                outlined
                placeholder="Type a message..."
                hide-details
                @input="handleTyping"
                class="message-field"
              >
                <template v-slot:append>
                  <v-btn type="submit" icon :disabled="!inputMessage.trim()">
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </form>
          </footer>
        </section>

        <!-- Mobile overlay when chat is open (for better UX) -->
        <!-- Move this inside the chat-container div, right before the closing tag -->
        <div
          v-if="activeChat && !isSidebarOpen && display.mobile.value"
          class="mobile-chat-overlay"
          @click="isSidebarOpen = true"
        ></div>
      </div>
    </div>
  </v-container>

  <!-- <FooterMobile v-if="display.mobile.value"/> -->
  <FooterComponent v-if="!display.mobile.value" />
</template>
<script>
import Header from "@/components/Header.vue";
import HeaderSUUQ from "@/components/HeaderSUUQ.vue";
import { useDisplay } from "vuetify";
import { API_URL } from "@/helpers/api";
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import ListingService from "@/services/listing.service";
import UsersService from "@/services/user.service";
import {
  collection,
  doc,
  query,
  onSnapshot,
  orderBy,
  updateDoc,
  setDoc,
  serverTimestamp,
  getDocs,
  addDoc,
  getDoc,
  limit,
} from "firebase/firestore";
import { db, rtdb } from "@/db";
import {
  ref as dbRef,
  onDisconnect,
  set,
  serverTimestamp as rtdbTimestamp,
  onValue,
} from "firebase/database";
import { debounce } from "lodash";
import moment from "moment";
import FooterComponent from "@/components/footer/FooterComponent.vue";
import FooterMobile from "@/components/footer/FooterMobile.vue";

export default {
  components: {
    Header,
    FooterComponent,
    HeaderSUUQ,
    FooterMobile,
  },
  name: "ChatView",
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const user = computed(() => store.state.auth.user);
    const imageUrl = API_URL;
    const display = useDisplay();

    const activeChats = ref([]);
    const activeChat = ref(null);
    const messages = ref([]);
    const loadingUsers = ref(false);
    const messagesLoading = ref(false);
    const isTyping = ref(false);
    const inputMessage = ref("");
    const chatBox = ref(null);
    const loading = ref(true);

    let messagesUnsubscribe = null;

    // const formatDate = (timestamp) => {
    //   if (!timestamp) return "";
    //   const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

    //   const day = date.getDate();
    //   const month = date.toLocaleString("default", { month: "long" });
    //   const hours = date.getHours() % 12 || 12;
    //   const minutes = date.getMinutes().toString().padStart(2, "0");
    //   const ampm = date.getHours() >= 12 ? "PM" : "AM";

    //   // Add ordinal suffix to day
    //   const suffixes = ["th", "st", "nd", "rd"];
    //   const v = day % 100;
    //   const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];

    //   return `${day}${suffix} ${month}, ${hours}:${minutes} ${ampm}`;
    // };

    const formatDate = (timestamp) => {
      if (!timestamp) return "";

      const now = new Date();
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);

      // Check if it's today
      if (isSameDay(date, now)) {
        // Format as time only for today's messages
        const hours = date.getHours() % 12 || 12;
        const minutes = date.getMinutes().toString().padStart(2, "0");
        const ampm = date.getHours() >= 12 ? "PM" : "AM";
        return `${hours}:${minutes} ${ampm}`;
      }

      // Check if it's yesterday
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      if (isSameDay(date, yesterday)) {
        const hours = date.getHours() % 12 || 12;
        const minutes = date.getMinutes().toString().padStart(2, "0");
        const ampm = date.getHours() >= 12 ? "PM" : "AM";
        return `Yesterday, ${hours}:${minutes} ${ampm}`;
      }

      // Full date format for older messages
      const day = date.getDate();
      const month = date.toLocaleString("default", { month: "long" });
      const hours = date.getHours() % 12 || 12;
      const minutes = date.getMinutes().toString().padStart(2, "0");
      const ampm = date.getHours() >= 12 ? "PM" : "AM";

      // Add ordinal suffix to day
      const suffixes = ["th", "st", "nd", "rd"];
      const v = day % 100;
      const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];

      return `${day}${suffix} ${month}, ${hours}:${minutes} ${ampm}`;
    };

    const isSameDay = (date1, date2) => {
      return (
        date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear()
      );
    };

    const getInitials = (name) =>
      name
        ?.split(" ")
        .map((n) => n[0])
        .join("")
        .substring(0, 2) || "??";

    const listenToUserStatus = (userId, chatObj) => {
      const statusRef = dbRef(rtdb, `status/${userId}`);
      onValue(statusRef, (snap) => {
        const val = snap.val();
        if (val) chatObj.isOnline = val.online;
      });
    };

    const loadActiveChats = () => {
      loadingUsers.value = true;
      const myListingsRef = collection(
        db,
        user.value.id.toString(),
        "chats",
        "listing"
      );

      onSnapshot(myListingsRef, async (snapshot) => {
        console.log("onSnapshot triggered, snapshot size:", snapshot.size);
        console.log(
          "Document changes:",
          snapshot.docChanges().map((change) => ({
            type: change.type,
            docId: change.doc.id,
            data: change.doc.data(),
          }))
        );
        const threads = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const listingId = docSnap.id;
            const docData = docSnap.data();

            console.log(`Processing chat ${listingId}:`, docData);

            // const msgsQ = query(
            //   collection(docSnap.ref, "chat"),
            //   orderBy("date", "desc"),
            //   limit(1)
            // );
            // const lastMsgSnap = await getDocs(msgsQ);
            // const lastDoc = lastMsgSnap.docs[0];
            // const last = lastDoc?.data();

            const last = {
              description: docData.lastMessage || "",
              date: docData.lastUpdated || null,
              sender_id: docData.lastSender || null,
              seenAt: docData.lastSeenAt || null,
            };

            console.log(`Latest message for ${listingId}:`, last);

            let otherUserId;
            if (last && last.sender_id) {
              // If we have sender_id, the other user is either the sender or from docData
              otherUserId =
                last.sender_id === user.value.id.toString()
                  ? docData.withUserId // If I sent it, other user is from withUserId
                  : last.sender_id; // If they sent it, other user is the sender
            } else {
              otherUserId = docData.withUserId;
              if (!otherUserId) return null;
            }

            const [otherUserRes, listingRes] = await Promise.all([
              UsersService.show(otherUserId),
              ListingService.show(listingId),
            ]);

            const chatObj = {
              id: listingId,
              seller: otherUserRes.data.data,
              listing: listingRes.data.data,
              lastMessage: last?.description || "",
              lastUpdated: last?.date || null,
              lastSeenAt: last?.seenAt || null,
              lastSender: last?.sender_id || null,
              unread: docData.unread?.[user.value.id] || 0,
              isOnline: false,
            };

            console.log("Whats here", chatObj);
            console.log("Built chatObj:", chatObj);

            listenToUserStatus(otherUserId, chatObj);
            return chatObj;
          })
        );

        activeChats.value = threads.filter(Boolean);

        activeChats.value = threads.filter(Boolean).sort((a, b) => {
          if (!a.lastUpdated && !b.lastUpdated) return 0;
          if (!a.lastUpdated) return 1;
          if (!b.lastUpdated) return -1;

          const aTime = a.lastUpdated.toDate
            ? a.lastUpdated.toDate().getTime()
            : new Date(a.lastUpdated).getTime();
          const bTime = b.lastUpdated.toDate
            ? b.lastUpdated.toDate().getTime()
            : new Date(b.lastUpdated).getTime();

          return bTime - aTime; // Most recent first
        });

        loadingUsers.value = false;
      });
    };

    const selectChatMobile = (chat) => {
      selectChat(chat);
      // On mobile, automatically show the chat area when a chat is selected
      if (display.mobile.value) {
        isSidebarOpen.value = false;
      }
    };

    const selectChat = async (chat) => {
      if (activeChat.value?.id === chat.id) return;

      if (messagesUnsubscribe) messagesUnsubscribe();

      activeChat.value = chat;
      messages.value = [];
      messagesLoading.value = true;

      const msgsCol = collection(
        db,
        user.value.id.toString(),
        "chats",
        "listing",
        chat.id,
        "chat"
      );
      const msgsQ = query(msgsCol, orderBy("date"));

      messagesUnsubscribe = onSnapshot(msgsQ, async (snapshot) => {
        const newMessages = [];

        // Process all existing messages
        snapshot.forEach((doc) => {
          newMessages.push({ id: doc.id, ...doc.data() });
        });

        // Process changes to update read status
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === "added") {
            const data = change.doc.data();

            // If message is from other user and unread, mark as read
            if (data.sender_id !== user.value.id.toString() && !data.isRead) {
              const basePath = [
                "chats",
                "listing",
                chat.id,
                "chat",
                change.doc.id,
              ];
              const path1 = doc(db, user.value.id.toString(), ...basePath);
              const path2 = doc(db, chat.seller.id.toString(), ...basePath);

              const [docSnap1, docSnap2] = await Promise.all([
                getDoc(path1),
                getDoc(path2),
              ]);

              if (docSnap1.exists() && docSnap2.exists()) {
                await Promise.all([
                  updateDoc(path1, {
                    isRead: true,
                    seenAt: serverTimestamp(),
                  }),
                  updateDoc(path2, {
                    isRead: true,
                    seenAt: serverTimestamp(),
                  }),
                ]);
              }
            }

            // If message is ours and was just sent, add temporary single tick
            if (data.sender_id === user.value.id.toString() && !data.isRead) {
              const messageIndex = newMessages.findIndex(
                (m) => m.id === change.doc.id
              );
              if (messageIndex > -1) {
                newMessages[messageIndex].justSent = true;
              }
            }
          }
        });

        // Sort messages by date
        messages.value = newMessages.sort((a, b) => {
          const aTime = a.date?.toDate?.()?.getTime?.() || 0;
          const bTime = b.date?.toDate?.()?.getTime?.() || 0;
          return aTime - bTime;
        });

        messagesLoading.value = false;

        nextTick(() => {
          if (chatBox.value) {
            chatBox.value.scrollTop = chatBox.value.scrollHeight;
          }
          scrollToBottom();
        });
      });
    };

    // const sendMessage = debounce(async () => {
    //   const content = inputMessage.value.trim();
    //   if (!content || !activeChat.value) return;

    //   const me = user.value.id.toString();
    //   const other = activeChat.value.seller.id.toString();
    //   const listing = activeChat.value.listing.id.toString();

    //   const payload = {
    //     sender_id: me,
    //     receipient: other,
    //     listing_id: listing,
    //     description: content,
    //     date: serverTimestamp(),
    //     isRead: false, // Initially false when sending
    //     status: true,
    //     title: user.value.fullName,
    //   };

    //   const doc1 = doc(db, me, "chats", "listing", listing);
    //   const doc2 = doc(db, other, "chats", "listing", listing);

    //   await Promise.all([
    //     setDoc(
    //       doc1,
    //       {
    //         withUserId: other,
    //         [`unread.${other}`]: 1, // Increment unread count for recipient
    //       },
    //       { merge: true }
    //     ),
    //     setDoc(
    //       doc2,
    //       {
    //         withUserId: me,
    //         [`unread.${me}`]: 0, // Reset unread count for sender
    //       },
    //       { merge: true }
    //     ),
    //   ]);

    //   const path1 = collection(doc1, "chat");
    //   const path2 = collection(doc2, "chat");

    //   await Promise.all([addDoc(path1, payload), addDoc(path2, payload)]);
    //   inputMessage.value = "";
    // }, 300);

    const sendMessage = debounce(async () => {
      const content = inputMessage.value.trim();
      if (!content || !activeChat.value) return;

      const me = user.value.id.toString();
      const other = activeChat.value.seller.id.toString();
      const listing = activeChat.value.listing.id.toString();

      const payload = {
        sender_id: me,
        receipient: other,
        listing_id: listing,
        description: content,
        date: serverTimestamp(),
        isRead: false, // Initially false when sending
        status: true,
        title: user.value.fullName,
      };

      const doc1 = doc(db, me, "chats", "listing", listing);
      const doc2 = doc(db, other, "chats", "listing", listing);

      await Promise.all([
        setDoc(
          doc1,
          {
            withUserId: other,
            [`unread.${other}`]: 1, // Increment unread count for recipient
          },
          { merge: true }
        ),
        setDoc(
          doc2,
          {
            withUserId: me,
            [`unread.${me}`]: 0, // Reset unread count for sender
          },
          { merge: true }
        ),
      ]);

      const path1 = collection(doc1, "chat");
      const path2 = collection(doc2, "chat");

      // Add messages to both users' chat subcollections
      await Promise.all([addDoc(path1, payload), addDoc(path2, payload)]);

      console.log("About to update main chat docs with:", {
        content: content,
        me: me,
        other: other,
        listing: listing,
      });

      // Update the main chat documents with the latest message info
      // This will trigger the onSnapshot listener in loadActiveChats()
      await Promise.all([
        updateDoc(doc1, {
          lastMessage: content,
          lastUpdated: serverTimestamp(),
          lastSender: me,
        }),
        updateDoc(doc2, {
          lastMessage: content,
          lastUpdated: serverTimestamp(),
          lastSender: me,
        }),
      ]);

      console.log("Main chat docs updated successfully");

      inputMessage.value = "";
    }, 300);

    const handleTyping = debounce(() => {
      if (!activeChat.value) return;
      const me = user.value.id.toString();
      const other = activeChat.value.seller.id.toString();
      const listing = activeChat.value.listing.id.toString();

      const path1 = doc(db, me, "chats", "listing", listing);
      const path2 = doc(db, other, "chats", "listing", listing);

      updateDoc(path1, { [`typing.${me}`]: true });
      updateDoc(path2, { [`typing.${me}`]: true });

      setTimeout(() => {
        updateDoc(path1, { [`typing.${me}`]: false });
        updateDoc(path2, { [`typing.${me}`]: false });
      }, 2000);
    }, 500);

    const updateOnlineStatus = () => {
      const statusRef = dbRef(rtdb, `status/${user.value.id}`);
      set(statusRef, { online: true, lastChanged: rtdbTimestamp() });
      onDisconnect(statusRef).update({
        online: false,
        lastChanged: rtdbTimestamp(),
      });
    };

    onMounted(() => {
      console.log("What are these active chats", activeChats.value);
      updateOnlineStatus();
      loadActiveChats();

      if (route.name === "ChatWindow") {
        const listingId = route.params.listingId.toString();
        const sellerId = route.params.sellerId.toString();

        const existing = activeChats.value.find(
          (c) =>
            c.id.toString() === listingId &&
            [c.seller.id.toString(), user.value.id.toString()].includes(
              sellerId
            )
        );

        console.log("Existing chats", existing);

        if (existing) {
          selectChat(existing);
        }
      }
      loading.value = false;
    });

    onUnmounted(() => {
      messagesUnsubscribe && messagesUnsubscribe();
    });

    const isSidebarOpen = ref(true);
    const toggleSidebar = () => {
      // on mobile, flip between showing list ↔ chat
      isSidebarOpen.value = !isSidebarOpen.value;
    };

    // Add this ref
    const messagesContainer = ref(null);

    // Add this method to scroll to bottom
    const scrollToBottom = () => {
      if (display.mobile.value && isSidebarOpen.value) return; // Don't scroll if sidebar is open on mobile
      nextTick(() => {
        if (messagesContainer.value) {
          messagesContainer.value.scrollTop =
            messagesContainer.value.scrollHeight;
        }
      });
    };

    watch(messages, scrollToBottom, { deep: true });

    return {
      user,
      activeChats,
      activeChat,
      messages,
      loadingUsers,
      messagesLoading,
      isTyping,
      inputMessage,
      chatBox,
      formatDate,
      getInitials,
      selectChat,
      selectChatMobile,
      sendMessage,
      handleTyping,
      imageUrl,
      toggleSidebar,
      isSidebarOpen,
      display,
      messagesContainer,
    };
  },
};
</script>

<style scoped>
/* Reset & Base */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html,
body,
#app {
  height: 100%;
  font-family: "Avenir", Helvetica, Arial, sans-serif;
}

/* Root grid: header / content / footer */
.view.chat {
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100vh;
  width: 100%;
  margin-left: 80px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

/* 1) Header */
.v-app-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  color: #fff;
}

/* 2) Main content */
.chat-container {
  display: grid;
  grid-template-columns: 500px 1fr;
  height: 100%;
  column-gap: 24px;
  overflow: hidden;
}

/* Messages pane */
.chat-box {
  padding: 16px;
  background: #fafafa;
  overflow-y: auto;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

/* Individual message */
.message {
  display: flex;
  margin-bottom: 16px;
  align-items: flex-end;
  flex-shrink: 0;
  min-height: 60px;
  transition: none;
}

.message.current-user {
  justify-content: flex-end;
}

.other-user-avatar {
  margin-right: 12px;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.other-user-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-fallback {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: darkgoldenrod;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
}

.message-bubble {
  padding: 12px 16px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  word-wrap: break-word;
  overflow-wrap: break-word;
  box-sizing: border-box;
  width: auto;
  max-width: 70%;
  min-width: 120px;
}

.message.current-user .message-bubble {
  background: darkgoldenrod;
  color: #fff;
  border-bottom-right-radius: 4px;
}

.message:not(.current-user) .message-bubble {
  border-bottom-left-radius: 4px;
}

.message-inner {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  position: relative;
  transition: transform 0.15s ease;
}

.message-inner:hover {
  transform: translateY(-2px);
}

.message.current-user .message-inner {
  background: darkgoldenrod;
  color: #fff;
}

.sender,
.username {
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 4px;
  opacity: 0.8;
}

.text,
.content {
  font-size: 1rem;
  line-height: 1.4;
}

.time,
.timestamp {
  font-size: 0.75rem;
  text-align: right;
  margin-top: 4px;
  opacity: 0.7;
  color: #999;
}

.message.current-user .time {
  color: rgba(255, 255, 255, 0.8);
}

.message.current-user .username {
  color: rgba(255, 255, 255, 0.8);
}

.seen-status {
  font-size: 0.7rem;
  color: #4caf50;
  font-style: italic;
  margin-top: 2px;
}

/* Typing indicator */
.typing-indicator {
  font-size: 0.9rem;
  color: #666;
  padding: 8px 16px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

/* Sidebar */
.user-list {
  background: #fff;
  border-left: 1px solid #eee;
  padding: 20px;
  overflow-y: auto;
}

.user-list h3 {
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.user-item {
  display: flex;
  align-items: center;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.user-item:hover {
  background: #f5f5f5;
}

.user-item.active {
  background: #e8f0fe;
}

.user-item:active {
  transform: scale(0.98);
  background-color: #f0f0f0;
}

.listing-thumb {
  flex-shrink: 0;
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 8px;
  margin-right: 12px;
}

.user-avatar {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: darkgoldenrod;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-weight: 500;
}

.online-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 10px;
  height: 10px;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #ccc;
}

.online-indicator.online {
  background: #4caf50;
}

.unread-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: darkgoldenrod;
  color: #fff;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-info {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 2px;
}

.listing-title {
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 2px;
}

.last-message {
  font-size: 0.9rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Footer */
.message-input {
  position: sticky;
  bottom: 0;
  background: #fff;
  padding: 20px 24px;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
}

.message-field .v-input__control {
  border-radius: 24px;
  background: #f5f5f5;
}

.meta {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
}

.read-receipts {
  display: flex;
  align-items: center;
}

.message.current-user .read-receipts .v-icon {
  color: rgba(255, 255, 255, 0.8);
}

.message:not(.current-user) .read-receipts .v-icon {
  color: rgba(0, 0, 0, 0.6);
}

/* Mobile layout improvements */
@media (max-width: 768px) {
  .view.chat {
    margin-left: 0;
    border-radius: 0;
    box-shadow: none;
    height: 100dvh;
  }

  .chat-container {
    display: block;
    height: 100%;
    scrollbar-width: none;
  }

  .user-list {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100dvh;
    background: #fff;
    z-index: 300;
    overflow-y: auto;
    padding: 16px 14px 24px;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    box-shadow: 8px 0 24px rgba(0, 0, 0, 0.12);
  }

  .user-list.show {
    display: block;
    transform: translateX(0);
  }

  .chat-header {
    position: sticky;
    top: 0;
    left: 0;
    height: 56px;
    padding: 0 12px;
    background: #fff;
    border-bottom: 1px solid #eee;
    z-index: 120;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .chat-header-title {
    font-weight: 600;
    font-size: 0.98rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .chat-box {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    background: #fafafa;
    height: 100dvh;
    overflow-y: auto !important;
    padding-bottom: calc(92px + env(safe-area-inset-bottom));
  }

  .message {
    margin: 6px 0;
  }

  .message-bubble {
    max-width: 88%;
    font-size: 0.95rem;
  }

  .message-input {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    padding: 10px 12px 12px;
    padding-bottom: calc(12px + env(safe-area-inset-bottom));
    background: #fff;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
    z-index: 350;
  }

  .message-field .v-input__control,
  .message-field .v-field {
    border-radius: 22px;
    background: #f5f5f5;
  }

  .d-md-none[icon] {
    touch-action: manipulation;
  }

  .user-item {
    padding: 10px 8px;
    margin-bottom: 8px;
    position: relative;
  }

  .user-item::after {
    content: "";
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-right: 2px solid #999;
    border-bottom: 2px solid #999;
    transform: translateY(-50%) rotate(-45deg);
    opacity: 0.6;
  }

  .listing-thumb {
    width: 46px;
    height: 46px;
  }

  .user-avatar {
    width: 38px;
    height: 38px;
  }

  .user-name {
    font-size: 0.95rem;
  }

  .listing-title {
    font-size: 0.85rem;
    color: #666;
  }

  .last-message {
    font-size: 0.82rem;
    color: #777;
  }

  .chat-box::-webkit-scrollbar,
  .user-list::-webkit-scrollbar {
    display: none;
  }

  .chat-box {
    scrollbar-width: none;
  }

  /* Mobile overlay for chat */
  .chat-box {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 400;
    background: white;
    transform: translateX(100%);
    transition: transform 0.3s ease;
    padding-top: 56px;
    overflow-y: auto !important;
    padding-bottom: 80px;
  }

  .chat-box.show {
    transform: translateX(0);
  }

  .mobile-chat-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
    z-index: 350;
    display: block;
  }

  .chat-header {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 56px;
    background: white;
    z-index: 450;
    display: flex;
    align-items: center;
    padding: 0 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .message-input {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 450;
    background: white;
    padding: 12px;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  }
  .messages-container {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    margin-top: 30px;
  }
}

/* Hide the chevron on desktop */
@media (min-width: 769px) {
  .user-item::after {
    display: none;
  }
}

/* Ultra-small devices */
@media (max-width: 480px) {
  .message-bubble {
    max-width: 90%;
    font-size: 0.95rem;
  }

  .chat-box {
    padding: 10px;
    padding-bottom: 80px;
    overflow-y: auto !important;
  }

  .timestamp {
    display: none;
  }

  .user-item {
    padding: 12px 10px;
  }

  .listing-thumb {
    width: 42px;
    height: 42px;
  }

  .user-avatar {
    width: 36px;
    height: 36px;
    font-size: 0.8rem;
  }

  .user-name {
    font-size: 0.9rem;
  }

  .last-message {
    font-size: 0.8rem;
  }
}
</style>
